import React from "react";
import { InlineMath } from "react-katex";

import PolylineGraphInput from "../../../../components/GraphPlotter/tasks/PolylineGraphInput";

const X_RANGE = [-6, 6];
const Y_RANGE = [-6, 6];

const absYVariants = [
  [
    { x: -5, y: -2, closed: false },
    { x: -3, y: 2, closed: true },
    { x: 1, y: -2, closed: true },
    { x: 4, y: 1, closed: false },
  ],
  [
    { x: -4, y: 2, closed: true },
    { x: -2, y: -2, closed: true },
    { x: 2, y: -2, closed: true },
    { x: 4, y: 2, closed: false },
  ],
  [
    { x: -5, y: 1, closed: true },
    { x: -2, y: -2, closed: true },
    { x: 0, y: 2, closed: true },
    { x: 4, y: -2, closed: false },
  ],
  [
    { x: -5, y: -1, closed: true },
    { x: -2, y: -1, closed: true },
    { x: 1, y: 2, closed: true },
    { x: 5, y: -2, closed: false },
  ],
  [
    { x: -4, y: 2, closed: false },
    { x: -1, y: -1, closed: true },
    { x: 2, y: -1, closed: true },
    { x: 5, y: 2, closed: true },
  ],
  [
    { x: -5, y: -2, closed: true },
    { x: -1, y: 2, closed: true },
    { x: 2, y: 2, closed: true },
    { x: 5, y: -1, closed: false },
  ],
  [
    { x: -5, y: 2, closed: false },
    { x: -3, y: -2, closed: true },
    { x: 1, y: -2, closed: true },
    { x: 3, y: 0, closed: true },
    { x: 5, y: 2, closed: true },
  ],
  [
    { x: -5, y: -1, closed: true },
    { x: -3, y: -1, closed: true },
    { x: -1, y: 1, closed: true },
    { x: 2, y: -2, closed: true },
    { x: 4, y: 0, closed: false },
  ],
  [
    { x: -4, y: -2, closed: false },
    { x: -2, y: 0, closed: true },
    { x: 0, y: 2, closed: true },
    { x: 2, y: 0, closed: true },
    { x: 4, y: -2, closed: true },
  ],
  [
    { x: -5, y: 0, closed: true },
    { x: -2, y: 2, closed: true },
    { x: 0, y: -2, closed: true },
    { x: 3, y: -2, closed: true },
    { x: 5, y: 2, closed: false },
  ],
];

const absXVariants = [
  [
    { x: -4, y: -1, closed: true },
    { x: -1, y: 2, closed: true },
    { x: 0, y: 1, closed: true },
    { x: 2, y: -1, closed: true },
    { x: 4, y: 2, closed: false },
  ],
  [
    { x: -5, y: 2, closed: false },
    { x: -2, y: -2, closed: true },
    { x: 0, y: 0, closed: true },
    { x: 2, y: 2, closed: true },
    { x: 5, y: -1, closed: false },
  ],
  [
    { x: -4, y: 1, closed: true },
    { x: -1, y: -2, closed: true },
    { x: 0, y: -1, closed: true },
    { x: 3, y: 2, closed: false },
  ],
  [
    { x: -5, y: -2, closed: true },
    { x: -2, y: 1, closed: true },
    { x: 0, y: 0, closed: true },
    { x: 2, y: -2, closed: true },
    { x: 5, y: 1, closed: false },
  ],
  [
    { x: -4, y: 2, closed: false },
    { x: -1, y: -1, closed: true },
    { x: 0, y: 2, closed: true },
    { x: 1, y: 2, closed: true },
    { x: 4, y: -2, closed: true },
  ],
  [
    { x: -5, y: 1, closed: true },
    { x: -3, y: -2, closed: true },
    { x: 0, y: -1, closed: true },
    { x: 2, y: 1, closed: true },
    { x: 5, y: 1, closed: false },
  ],
  [
    { x: -4, y: -2, closed: false },
    { x: -2, y: 2, closed: true },
    { x: 0, y: 1, closed: true },
    { x: 1, y: -2, closed: true },
    { x: 3, y: 2, closed: true },
  ],
  [
    { x: -5, y: 0, closed: true },
    { x: -1, y: -2, closed: true },
    { x: 0, y: 0, closed: true },
    { x: 2, y: 2, closed: true },
    { x: 4, y: 0, closed: false },
  ],
];

function pick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function normalizePoint(point, index, length) {
  return {
    x: Number(point.x),
    y: Number(point.y),
    closed: index === 0 || index === length - 1 ? point.closed !== false : true,
  };
}

function zeroCrossing(a, b) {
  if (a.y === 0 || b.y === 0 || Math.sign(a.y) === Math.sign(b.y)) return null;
  const t = -a.y / (b.y - a.y);
  const x = a.x + t * (b.x - a.x);
  if (!Number.isInteger(x)) return null;
  return { x, y: 0, closed: true };
}

function hasPoint(points, candidate) {
  return points.some((point) => point.x === candidate.x && point.y === candidate.y);
}

function transformAbsY(points) {
  const result = [];

  points.forEach((point, index) => {
    if (index > 0) {
      const crossing = zeroCrossing(points[index - 1], point);
      if (crossing && !hasPoint(result, crossing)) result.push(crossing);
    }

    const transformed = { x: point.x, y: Math.abs(point.y), closed: point.closed };
    if (!hasPoint(result, transformed)) result.push(transformed);
  });

  return result.sort((a, b) => a.x - b.x);
}

function transformAbsX(points) {
  const right = points.filter((point) => point.x >= 0).sort((a, b) => a.x - b.x);
  const mirrored = right
    .filter((point) => point.x > 0)
    .map((point) => ({ ...point, x: -point.x }))
    .reverse();

  return [...mirrored, ...right].map((point, index, arr) => ({
    ...point,
    closed: index === 0 || index === arr.length - 1 ? point.closed !== false : true,
  }));
}

function pointsEqual(userPoints = [], expectedPoints = []) {
  if (!Array.isArray(userPoints) || userPoints.length !== expectedPoints.length) return false;

  return expectedPoints.every((expected, index) => {
    const got = normalizePoint(userPoints[index], index, userPoints.length);
    const want = normalizePoint(expected, index, expectedPoints.length);
    return got.x === want.x && got.y === want.y && got.closed === want.closed;
  });
}

export function generateTask(option) {
  const mode = option === "absX" || option === "absY" ? option : pick(["absY", "absX"]);
  const base = mode === "absX" ? pick(absXVariants) : pick(absYVariants);
  const expected = mode === "absX" ? transformAbsX(base) : transformAbsY(base);

  return {
    type: "AbsoluteValuePolylineSketchTask",
    data: {
      mode,
      base,
      expected,
      xRange: X_RANGE,
      yRange: Y_RANGE,
    },
  };
}

function formula(mode) {
  return mode === "absX" ? "y=f(|x|)" : "y=|f(x)|";
}

export function renderTask({ data }) {
  return (
    <div style={{ marginTop: 6 }}>
      <p>
        Na podstawie fioletowego wykresu funkcji <InlineMath math={"f"} /> dorysuj pomarańczowy wykres funkcji{" "}
        <InlineMath math={formula(data.mode)} /> na tym samym układzie współrzędnych.
      </p>
    </div>
  );
}

export function renderInput(task, userInput = {}, onChange, disabled) {
  return (
    <PolylineGraphInput
      value={userInput}
      onChange={onChange}
      disabled={disabled}
      xRange={task.data.xRange}
      yRange={task.data.yRange}
      referencePoints={task.data.base}
      referenceColor="#8b5cf6"
      answerColor="#ff9900"
    />
  );
}

function ExercisePolylineInput({ task }) {
  const [value, setValue] = React.useState({ points: [] });
  return renderInput(task, value, setValue, false);
}

export function renderExerciseTask(task) {
  return (
    <div>
      {renderTask(task)}
      <ExercisePolylineInput task={task} />
    </div>
  );
}

export function checkAnswer(task, userInput = {}) {
  return pointsEqual(userInput.points, task.data.expected);
}

export function renderAnswer({ data }) {
  return (
    <PolylineGraphInput
      value={{ points: data.expected }}
      disabled
      xRange={data.xRange}
      yRange={data.yRange}
      referencePoints={data.base}
      referenceColor="#8b5cf6"
      answerColor="#ff9900"
      showControls={false}
    />
  );
}

export function renderExplanation({ data }) {
  const isAbsX = data.mode === "absX";
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      {isAbsX ? (
        <>
          <p><strong>Krok 1.</strong> Zostaw prawą część wykresu funkcji <InlineMath math={"f"} />.</p>
          <p><strong>Krok 2.</strong> Odbij tę część względem osi <InlineMath math={"OY"} />.</p>
        </>
      ) : (
        <>
          <p><strong>Krok 1.</strong> Część nad osią <InlineMath math={"OX"} /> zostaje bez zmian.</p>
          <p>
            <strong>Krok 2.</strong> Część pod osią <InlineMath math={"OX"} /> odbijamy do góry. Jeżeli odcinek
            przecina oś <InlineMath math={"OX"} />, punkt przecięcia zostaje na osi.
          </p>
        </>
      )}
      {renderAnswer({ data })}
    </div>
  );
}

export function renderUserAnswer(arg1 = {}, arg2 = {}) {
  const firstIsTask = arg1?.type === "AbsoluteValuePolylineSketchTask";
  const task = firstIsTask ? arg1 : arg2?.type === "AbsoluteValuePolylineSketchTask" ? arg2 : null;
  const userInput = firstIsTask ? arg2 : arg1;
  const points = Array.isArray(userInput?.points) ? userInput.points : [];
  if (!points.length) return <i>(brak odpowiedzi)</i>;
  if (!task?.data) return <span>{points.map((point) => "(" + point.x + ", " + point.y + ")").join(" - ")}</span>;

  return (
    <div style={{ marginTop: 8 }}>
      <PolylineGraphInput
        value={{ points }}
        disabled
        xRange={task.data.xRange}
        yRange={task.data.yRange}
        referencePoints={task.data.base}
        referenceColor="#8b5cf6"
        answerColor="#ff9900"
        showControls={false}
      />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  renderExerciseTask,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
